import {
  capitalize,
  distinctArray,
  isValidFileName,
} from '@elementary/common-ui/utils/common';
import { UseFormReturn, UseFormWatch } from 'react-hook-form';
import {
  AddTestForm,
  SeverityFormField,
  Test,
  TestManagementCloudTest,
} from 'shared/types';
import { TRAINING_PERIOD_LIMITS } from 'shared/constants';

const isTrainingPeriodValid = (watch: UseFormWatch<AddTestForm>): boolean => {
  const tp = watch('anomalyConfig.trainingPeriod');
  if (!tp || tp.count == null || isNaN(Number(tp.count))) return false;
  const limits = TRAINING_PERIOD_LIMITS[tp.period];
  if (!limits) return false;
  return Number(tp.count) >= limits.min && Number(tp.count) <= limits.max;
};

export const getConditionalSeverityString = (
  severity?: SeverityFormField,
): {
  warnIf?: string;
  errorIf?: string;
} => {
  const { conditionalWarning, conditionalError } = severity || {};

  if (!conditionalWarning && !conditionalError) {
    return {};
  }

  const resultValue = { warnIf: '', errorIf: '' };

  if (conditionalWarning?.value !== undefined && conditionalWarning?.operator) {
    resultValue.warnIf = `${conditionalWarning.operator} ${conditionalWarning.value}`;
  }

  if (conditionalError?.value !== undefined && conditionalError?.operator) {
    resultValue.errorIf = `${conditionalError.operator} ${conditionalError.value}`;
  }

  return resultValue;
};

export const isStageValid = (
  methods: UseFormReturn<AddTestForm, any, undefined>,
  index: number,
  isEditMode?: boolean,
  isTestConfigEdited?: boolean,
  isTicketRequired?: boolean,
) => {
  const { watch } = methods;

  const isTargetSelectionStage =
    watch('testType') !== 'customQuery' && !isEditMode && index === 1;

  const isCustomQueryStage = watch('testType') === 'customQuery' && index === 1;

  const isConfigStage = isEditMode
    ? index === 1
    : watch('testType') === 'customQuery'
    ? index === 2
    : index === 3;

  const isSelectTestStage =
    !isEditMode && watch('testType') !== 'customQuery' && index === 2;

  const isReviewStage =
    (watch('testType') === 'customQuery' && index === 3) ||
    (isEditMode && index === 2) ||
    index === 4;

  if (isTargetSelectionStage) {
    return !!(watch('resources')?.length > 0);
  }

  if (isSelectTestStage) {
    return !!watch('testType');
  }

  if (isCustomQueryStage) {
    return watch('customQuery')?.length > 0;
  }

  if (isConfigStage) {
    if (watch('testType') === 'customQuery') {
      return isValidFileName(watch('testName')) && !!watch('path');
    }

    if (watch('testType') === 'customQuery') {
      return true;
    }

    if (watch('testType') === 'yamlTest') {
      const isYamlValid = !!watch('yaml') && watch('yamlValid');

      if (isEditMode) {
        return isYamlValid && isTestConfigEdited;
      }
      return isYamlValid;
    }

    if (
      watch('testType') === 'freshnessAnomalies' ||
      watch('testType') === 'volumeAnomalies'
    ) {
      return !!(
        watch('timestampColumn')?.length > 0 &&
        watch('detectionPeriod') > 0 &&
        watch('detectionDaysBack') > 0
      );
    }

    if (watch('testType') === 'cloudVolumeAnomalies') {
      return !!(
        watch('anomalyConfig.anomalyDirection') &&
        watch('anomalyConfig.sensitivity') &&
        watch('severity')?.type &&
        Number(watch('anomalyConfig.detectionPeriod.count')) > 0 &&
        watch('anomalyConfig.detectionPeriod.period') &&
        isTrainingPeriodValid(watch)
      );
    }

    if (watch('testType') === 'cloudFreshnessAnomalies') {
      return !!(
        (watch('anomalyConfig.fixedThreshold') ||
          watch('anomalyConfig.sensitivity')) &&
        watch('severity')?.type &&
        isTrainingPeriodValid(watch)
      );
    }

    if (watch('testType') === 'cloudPipelineTaskPerformance') {
      if (watch('anomalyConfig.fixedThreshold') != null) {
        return !!(
          (watch('anomalyConfig.fixedThreshold.count') || 0) > 0 &&
          watch('severity')?.type
        );
      }
      return !!(
        watch('anomalyConfig.sensitivity') &&
        watch('severity')?.type &&
        isTrainingPeriodValid(watch)
      );
    }

    if (watch('testType') === 'eventFreshnessAnomalies') {
      return !!(
        watch('timestampColumn')?.length > 0 &&
        watch('updatedAtTimestampColumn')?.length > 0 &&
        watch('detectionPeriod') > 0 &&
        watch('detectionDaysBack') > 0
      );
    }

    if (watch('testType') === 'dimensionAnomalies') {
      return !!(
        watch('timestampColumn')?.length > 0 &&
        watch('columnGroup')?.length > 0 &&
        watch('detectionPeriod') > 0 &&
        watch('detectionDaysBack') > 0
      );
    }

    if (
      watch('testType') === 'schemaChanges' ||
      watch('testType') === 'unique' ||
      watch('testType') === 'notNull'
    ) {
      return true;
    }

    if (watch('testType') === 'cloudDataContract') {
      const mode = watch('baselineMode') || 'complete';
      if (mode !== 'manual') {
        return true;
      }
      const cols = watch('baselineColumns');
      return !!(
        cols &&
        cols.length > 0 &&
        cols.some((c) => c.column_name && c.data_type)
      );
    }

    if (watch('testType') === 'acceptedValues') {
      return watch('values')?.length > 0;
    }

    if (watch('testType') === 'relationship') {
      return !!watch('tableName') && !!watch('columnName');
    }
  }

  if (isReviewStage) {
    if (isTicketRequired && !watch('ticketId')) return false;
    return true;
  }

  return false;
};

export const getConditionalSeverityValue = (
  watch: UseFormWatch<AddTestForm>,
):
  | {
      severity?: 'conditional';
      warn_if?: string;
      error_if?: string;
    }
  | undefined => {
  const { conditionalWarning, conditionalError } = watch('severity') || {};

  if (!conditionalWarning && !conditionalError) {
    return undefined;
  }

  const isDefaultValueSelected =
    !conditionalWarning &&
    conditionalError?.operator === '!=' &&
    conditionalError.value === 0;

  if (isDefaultValueSelected) {
    return undefined;
  }

  const resultValue: {
    warn_if?: string;
    error_if?: string;
  } = {};

  if (conditionalWarning?.value !== undefined && conditionalWarning?.operator) {
    resultValue.warn_if = `${conditionalWarning.operator}${conditionalWarning.value}`;
  }

  if (conditionalError?.value !== undefined && conditionalError?.operator) {
    resultValue.error_if = `${conditionalError.operator}${conditionalError.value}`;
  }

  return { ...resultValue, severity: 'conditional' };
};

export const getTestArgs = (data: AddTestForm) => {
  switch (data.testType) {
    case 'freshnessAnomalies':
      return {
        timestamp_column: data.timestampColumn,
        time_bucket: {
          period: data.detectionUnit,
          count: data.detectionPeriod,
        },
        sensitivity: data.sensitivity,
        days_back: data.detectionDaysBack,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
      };
    case 'volumeAnomalies':
      return {
        timestamp_column: data.timestampColumn,
        time_bucket: {
          period: data.detectionUnit,
          count: data.detectionPeriod,
        },
        sensitivity: data.sensitivity,
        days_back: data.detectionDaysBack,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
        anomaly_direction:
          data.detectionEventType === 'anomaly'
            ? null
            : data.detectionEventType,
      };
    case 'eventFreshnessAnomalies':
      return {
        event_timestamp_column: data.timestampColumn,
        update_timestamp_column: data.updatedAtTimestampColumn,
        time_bucket: {
          period: data.detectionUnit,
          count: data.detectionPeriod,
        },
        sensitivity: data.sensitivity,
        days_back: data.detectionDaysBack,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
      };
    case 'dimensionAnomalies':
      return {
        timestamp_column: data.timestampColumn,
        time_bucket: {
          period: data.detectionUnit,
          count: data.detectionPeriod,
        },
        sensitivity: data.sensitivity,
        days_back: data.detectionDaysBack,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        dimensions: data.columnGroup,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
        anomaly_direction:
          data.detectionEventType === 'anomaly'
            ? null
            : data.detectionEventType,
      };
    case 'unique':
    case 'notNull':
      return {};
    case 'acceptedValues':
      return {
        values: data.values,
        quote: data.valuesAreNotStrings ? false : null,
      };
    case 'relationship':
      return {
        field: data.columnName,
        to: data.tableName,
      };
    default:
      return {};
  }
};

export const isOnlyCustomQueryTests = (tests: Test[]) =>
  tests.every((item: Test) => item.is_singular);

export const normalizeCloudTest = (test: TestManagementCloudTest): Test => {
  const normalizedTest: Test = {
    id: test.id,
    asset_id: test.tableAsset.id,
    database_name: test.tableAsset.dbName,
    schema: test.tableAsset.schemaName,
    table: test.tableAsset.tableName,
    last_data_modification_time: test.tableAsset.lastDataModificationTime,
    materialization: test.tableAsset.materialization,
    column:
      test.resource.type === 'COLUMN'
        ? test.resource.name.split('.')[3]
        : undefined,
    package: 'elementary',
    name: test.name,
    type: test.type,
    source_type: 'cloud',
    severity: capitalize(test.severity.toLowerCase()),
    tags: distinctArray([
      ...(test.tags || []),
      ...(test?.tableAsset?.tags || []),
    ]),
    owners: distinctArray([
      ...(test.owners || []),
      ...(test?.tableAsset?.owners || []),
    ]),
    args: {},
    updated_at: test.updatedAt,
    meta: {},
    is_singular: false,
    original_path: '',
    is_cloud: true,
    muted: test.config.muted,
    channels: test.alertDestinations?.map((dest) => dest.channel),
    alert_destinations: test.alertDestinations?.map((dest) => ({
      id: dest.id,
      channel: dest.channel,
      type: dest.type,
      configuration_type: dest.configurationType,
      rule_id: dest.ruleId,
    })),
    alert_destinations_type: test.alertDestinations?.[0]?.configurationType, // should be same type for all destinations
  };

  return normalizedTest;
};
